import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    RiCloseLine, RiUploadCloudLine, RiDeleteBinLine, RiLoader4Line,
    RiFileWordLine, RiFilePptLine, RiFileExcelLine, RiFilePdfLine, RiFileTextLine,
} from 'react-icons/ri'
import toast from 'react-hot-toast'
import { useDocumentImport } from '@/api/useImport'
import { buildRoute } from '@/constants/routes'
import styles from './DocumentImportModal.module.css'

interface DocumentImportModalProps {
    visible: boolean
    onClose: () => void
}

interface FileEntry {
    file: File
    preview: string
    isDoc: boolean
}

function stripExtension(filename: string): string {
    return filename.replace(/\.[^/.]+$/, '')
}

export function DocumentImportModal({ visible, onClose }: DocumentImportModalProps) {
    const navigate = useNavigate()
    const importMutation = useDocumentImport()

    const [files, setFiles] = useState<FileEntry[]>([])
    const [projectName, setProjectName] = useState('')
    const [dragging, setDragging] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const ACCEPTED_IMAGES = ['image/png', 'image/jpeg', 'image/webp']
    const ACCEPTED_DOCS = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',   // .docx
        'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',         // .xlsx
        'application/msword',                                                         // .doc
        'application/vnd.ms-powerpoint',                                              // .ppt
        'application/vnd.ms-excel',                                                   // .xls
        'application/vnd.oasis.opendocument.text',                                    // .odt
        'application/vnd.oasis.opendocument.presentation',                            // .odp
        'application/vnd.oasis.opendocument.spreadsheet',                             // .ods
        'application/rtf',                                                            // .rtf
    ]
    const ACCEPTED_EXTENSIONS = ['.docx', '.pptx', '.xlsx', '.doc', '.ppt', '.xls', '.odt', '.odp', '.ods', '.rtf', '.pdf']
    const ALL_ACCEPTED = [...ACCEPTED_IMAGES, ...ACCEPTED_DOCS]

    const addFiles = useCallback((incoming: File[]) => {
        const valid = incoming.filter((f) => {
            if (ALL_ACCEPTED.includes(f.type)) return true
            // Fallback: check extension (some browsers don't set MIME correctly)
            const ext = f.name.toLowerCase().split('.').pop()
            return ext ? ACCEPTED_EXTENSIONS.includes(`.${ext}`) : false
        })
        if (valid.length === 0) return

        const entries: FileEntry[] = valid.map((file) => {
            const isImage = ACCEPTED_IMAGES.includes(file.type)
            return {
                file,
                preview: isImage ? URL.createObjectURL(file) : '',
                isDoc: !isImage,
            }
        })

        setFiles((prev) => {
            const next = [...prev, ...entries]
            if (prev.length === 0 && next.length > 0) {
                setProjectName(stripExtension(next[0].file.name))
            }
            return next
        })
    }, [])

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            addFiles(Array.from(e.target.files))
            e.target.value = ''
        }
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setDragging(false)
        if (e.dataTransfer.files) {
            addFiles(Array.from(e.dataTransfer.files))
        }
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setDragging(true)
    }

    const handleDragLeave = () => setDragging(false)

    const getDocIcon = (name: string) => {
        const ext = name.toLowerCase().split('.').pop()
        if (ext === 'pdf') return RiFilePdfLine
        if (ext === 'docx' || ext === 'doc' || ext === 'odt' || ext === 'rtf') return RiFileWordLine
        if (ext === 'pptx' || ext === 'ppt' || ext === 'odp') return RiFilePptLine
        if (ext === 'xlsx' || ext === 'xls' || ext === 'ods') return RiFileExcelLine
        return RiFileTextLine
    }

    const removeFile = (index: number) => {
        setFiles((prev) => {
            if (prev[index].preview) URL.revokeObjectURL(prev[index].preview)
            const next = prev.filter((_, i) => i !== index)
            if (index === 0 && next.length > 0) {
                setProjectName(stripExtension(next[0].file.name))
            }
            if (next.length === 0) {
                setProjectName('')
            }
            return next
        })
    }

    const handleClose = () => {
        files.forEach((f) => { if (f.preview) URL.revokeObjectURL(f.preview) })
        setFiles([])
        setProjectName('')
        onClose()
    }

    const handleImport = async () => {
        if (files.length === 0) return

        const formData = new FormData()
        files.forEach((entry) => formData.append('files', entry.file))
        formData.append('title', projectName || stripExtension(files[0].file.name))

        try {
            const project = await importMutation.mutateAsync(formData)
            toast.success(`"${project.name}" importado (${project.pages.length} página${project.pages.length !== 1 ? 's' : ''})`)
            handleClose()
            navigate(buildRoute.editor(project.id))
        } catch {
            toast.error('Error al importar el documento')
        }
    }

    if (!visible) return null

    const isLoading = importMutation.isPending

    return (
        <div className={styles.overlay} onClick={handleClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={styles.headerIcon}>
                            <RiUploadCloudLine />
                        </div>
                        <h2 className={styles.title}>Importar documento</h2>
                    </div>
                    <button className={styles.closeBtn} onClick={handleClose} disabled={isLoading}>
                        <RiCloseLine />
                    </button>
                </div>

                {/* Body */}
                <div className={styles.body}>
                    {/* Drop zone */}
                    <div
                        className={`${styles.dropZone} ${dragging ? styles.dragging : ''}`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,application/pdf,.docx,.pptx,.xlsx,.doc,.ppt,.xls,.odt,.odp,.ods,.rtf"
                            multiple
                            className={styles.hiddenInput}
                            onChange={handleFileInput}
                        />
                        <RiUploadCloudLine className={styles.dropIcon} />
                        <p className={styles.dropText}>
                            Arrastra archivos aquí o <span className={styles.dropLink}>selecciona archivos</span>
                        </p>
                        <p className={styles.dropHint}>PDF, DOCX, PPTX, XLSX, imágenes y más. Cada página se convierte en una página editable.</p>
                    </div>

                    {/* File previews */}
                    {files.length > 0 && (
                        <div className={styles.previewGrid}>
                            {files.map((entry, i) => {
                                const DocIcon = entry.isDoc ? getDocIcon(entry.file.name) : null
                                return (
                                <div key={i} className={styles.previewItem}>
                                    {entry.isDoc && DocIcon ? (
                                        <div className={styles.docThumb}>
                                            <DocIcon />
                                        </div>
                                    ) : (
                                        <img
                                            src={entry.preview}
                                            alt={entry.file.name}
                                            className={styles.previewThumb}
                                        />
                                    )}
                                    <button
                                        className={styles.removeBtn}
                                        onClick={() => removeFile(i)}
                                        title="Quitar"
                                        disabled={isLoading}
                                    >
                                        <RiDeleteBinLine />
                                    </button>
                                    <span className={styles.previewLabel}>{entry.file.name}</span>
                                </div>
                                )
                            })}
                        </div>
                    )}

                    {/* Project name input */}
                    {files.length > 0 && (
                        <div className={styles.nameField}>
                            <label className={styles.nameLabel} htmlFor="doc-import-name">
                                Nombre del proyecto
                            </label>
                            <input
                                id="doc-import-name"
                                className={styles.nameInput}
                                type="text"
                                value={projectName}
                                onChange={(e) => setProjectName(e.target.value)}
                                placeholder="Nombre del proyecto"
                                disabled={isLoading}
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <button className={styles.cancelBtn} onClick={handleClose} disabled={isLoading}>
                        Cancelar
                    </button>
                    <button
                        className={styles.importBtn}
                        onClick={handleImport}
                        disabled={files.length === 0 || isLoading}
                    >
                        {isLoading ? (
                            <>
                                <RiLoader4Line className={styles.spinner} />
                                Importando...
                            </>
                        ) : (
                            <>
                                <RiUploadCloudLine />
                                Importar
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}
